import { useState, useCallback, useRef, useEffect } from 'react';
import { useStreamingMediaRecorder } from './useStreamingMediaRecorder';
import { useDraftTranscriptionSSE } from './useSSE';
import { useToast } from '../contexts/ToastContext';
import api from '../api';
import { isSpendCapError, spendCapToastMessage } from '../utils/spendCap';
import { isAiUsageRefusedError, aiUsageRefusalScope } from '../utils/aiUsage';
import { onPageReturn } from '../utils/pageReturn';
import { playErrorSound } from '../utils/errorSound';

/**
 * Play an urgent mic-interruption alert: the descending error motif once,
 * louder — the recording just auto-paused because the OS took the mic
 * (phone call, lock screen), so the user needs to notice within seconds,
 * not 20 minutes later (#245). Best-effort: during an actual call the OS
 * may not route app audio at all, which is why the caller re-fires this
 * when the page returns to the foreground.
 */
function playInterruptionAlert() {
  try {
    const ctx = new (window.AudioContext || window.webkitAudioContext)();
    // The context can be suspended when the tab is backgrounded — resume it.
    if (ctx.state === 'suspended') ctx.resume();
    const playTone = (freq, startTime, duration) => {
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      osc.connect(gain);
      gain.connect(ctx.destination);
      osc.frequency.value = freq;
      osc.type = 'square';
      gain.gain.setValueAtTime(0.25, startTime);
      gain.gain.exponentialRampToValueAtTime(0.01, startTime + duration);
      osc.start(startTime);
      osc.stop(startTime + duration);
    };
    const now = ctx.currentTime;
    playTone(660, now, 0.15);
    playTone(440, now + 0.18, 0.15);
    playTone(330, now + 0.36, 0.25);
    setTimeout(() => ctx.close(), 1000);
  } catch (e) {
    console.warn('[StreamingTranscription] Could not play interruption alert:', e);
  }
}

// #320: shown when the session this tab records was ended somewhere else
// (another tab recovered or discarded it). Nothing more can go into it.
const SESSION_ENDED_MESSAGE =
  'This recording was ended from another tab or window, so recording stopped here.';

/**
 * Tell the server this tab no longer records the session, so another view
 * may recover it at once instead of after the server's liveness window
 * (#320). sendBeacon survives page unload; the POST is the fallback.
 */
function releaseSession(sessionId) {
  try {
    if (navigator.sendBeacon && navigator.sendBeacon(
      `${process.env.REACT_APP_BACKEND_URL || ''}/api/drafts/streaming/${sessionId}/release`
    )) {
      return;
    }
  } catch (_) { /* fall through to the POST */ }
  api.post(`/drafts/streaming/${sessionId}/release`).catch(() => {});
}

/**
 * useStreamingTranscription - Complete streaming transcription workflow (Draft-based).
 *
 * This hook orchestrates the entire streaming transcription process using drafts:
 * 1. Initialize a streaming session (creates a Draft, NOT a node)
 * 2. Start recording with chunked output
 * 3. Upload chunks as they're captured
 * 4. Receive transcription results via SSE (text appears in draft in real-time)
 * 5. Finalize when recording stops (transcript stays in draft)
 * 6. User can then edit the draft and save it as a node when ready
 *
 * No node is created until the user explicitly saves the draft.
 *
 * @param {Object} options
 * @param {number} options.parentId - Parent node ID (optional)
 * @param {string} options.privacyLevel - Privacy level for the eventual node
 * @param {string} options.aiUsage - AI usage setting for the eventual node
 * @param {number} options.chunkIntervalMs - Chunk interval (default: 5 minutes)
 * @param {Function} options.onTranscriptUpdate - Called with updated transcript text
 * @param {Function} options.onComplete - Called when transcription is complete
 * @param {Function} options.onError - Called on errors
 */
export function useStreamingTranscription(options = {}) {
  const {
    parentId = null,
    privacyLevel = 'private',
    aiUsage = 'none',
    chunkIntervalMs = 15 * 1000, // 15 seconds — frequent uploads for safety
    label = null, // e.g. "Reflect", "Orient" — used as title instead of "Voice note"
    onTranscriptUpdate = null,
    onComplete = null,
    onError = null,
  } = options;

  // State
  const [sessionState, setSessionState] = useState('idle'); // idle, initializing, recording, finalizing, complete, error
  const [draftId, setDraftId] = useState(null);
  const [sessionId, setSessionId] = useState(null);
  const [transcript, setTranscript] = useState('');
  const [uploadedChunks, setUploadedChunks] = useState(0);
  const [transcribedChunks, setTranscribedChunks] = useState(0);
  const [errorMessage, setErrorMessage] = useState(null);

  // Refs for tracking state across callbacks
  const sessionIdRef = useRef(null);
  const draftIdRef = useRef(null);
  const totalChunksRef = useRef(0);
  const pendingUploadsRef = useRef([]); // Track in-flight upload promises
  const failedChunksRef = useRef([]); // Track chunks that failed all retries
  const onCompleteRef = useRef(onComplete);
  onCompleteRef.current = onComplete;
  // #320: true once this tab asked the server to finalize the session.
  // all_complete before that means the session was ended elsewhere.
  const finalizeRequestedRef = useRef(false);
  // The session already stopped as ended elsewhere (dedups the several
  // in-flight chunk uploads that all come back rejected).
  const endedSessionRef = useRef(null);
  // The session this tab already released (pagehide, unmount and cancel
  // can all fire for the same departure).
  const releasedSessionRef = useRef(null);
  // Filled in once the recorder and SSE hooks below have returned, like
  // resetMediaRecorderRef.
  const stopMediaRecorderRef = useRef(null);
  const resetSSERef = useRef(null);

  // #320: this tab stops recording a session it never finalized (reload,
  // close, navigating away, cancel): tell the server, so the session can
  // be recovered at once rather than after the server's liveness window.
  const releaseIfAbandoned = useCallback(() => {
    const current = sessionIdRef.current;
    if (!current || finalizeRequestedRef.current
        || endedSessionRef.current === current
        || releasedSessionRef.current === current) {
      return;
    }
    releasedSessionRef.current = current;
    releaseSession(current);
  }, []);

  // Indirection ref: uploadChunk (defined below) needs to stop the recorder
  // on a fatal upload error, but the recorder's reset fn isn't in scope yet
  // because useStreamingMediaRecorder is called *after* uploadChunk. Wire it
  // up via a ref that gets populated once the recorder hook has returned.
  const resetMediaRecorderRef = useRef(null);

  // Network status
  const [isOffline, setIsOffline] = useState(!navigator.onLine);

  // #245: mic-interruption alert plumbing. The toast is long-lived (cleared
  // when the interruption ends), so the user who looks at the phone 20
  // minutes later still sees WHY the recording paused.
  const { addToast, removeToast } = useToast();
  const interruptionToastRef = useRef(null);

  const handleInterruption = useCallback(() => {
    console.warn('[StreamingTranscription] Mic interruption detected — alerting user');
    playInterruptionAlert();
    if (interruptionToastRef.current) removeToast(interruptionToastRef.current);
    interruptionToastRef.current = addToast(
      'Recording paused — another app took the microphone (phone call?). '
      + 'Everything up to the interruption is saved. Press Resume to continue.',
      24 * 60 * 60 * 1000
    );
  }, [addToast, removeToast]);

  // #320: the session was ended elsewhere — another tab recovered it
  // (all_complete arrived before this tab finalized) or discarded it
  // (uploads come back session_not_found / session_not_active). Stop the
  // recorder so the mic turns off and no chunk is sent into a dead
  // session, and report it as a fatal error rather than a completed
  // recording: the transcript is unfinished, and in voice mode onComplete
  // would send it to the LLM.
  //
  // Stop, not reset: stopping keeps the recorded audio in memory, so text
  // mode's "Save audio" can still download it — after a discard elsewhere
  // it is the only complete copy. The final chunk's upload is rejected
  // and deduplicated here. The SSE state is reset because it marked
  // itself complete, which would switch off its reconnect and stale
  // checks for this tab's next recording.
  const stopEndedSession = useCallback((endedSessionId) => {
    if (!endedSessionId || endedSessionRef.current === endedSessionId) return;
    endedSessionRef.current = endedSessionId;
    console.warn(`[StreamingTranscription] Session ${endedSessionId} was ended elsewhere — stopping the recorder`);
    if (stopMediaRecorderRef.current) {
      stopMediaRecorderRef.current();
    }
    if (resetSSERef.current) {
      resetSSERef.current();
    }
    failedChunksRef.current = failedChunksRef.current.filter(
      (c) => c.sessionId !== endedSessionId);
    setSessionState('error');
    setErrorMessage(SESSION_ENDED_MESSAGE);
    playErrorSound();
    if (onError) {
      const endedErr = new Error(SESSION_ENDED_MESSAGE);
      try {
        endedErr.fatal = true;
        endedErr.sessionEnded = true;
      } catch (_) { /* sealed */ }
      onError(endedErr);
    }
  }, [onError]);

  /**
   * Upload a single chunk with exponential backoff retry.
   * Returns { success: true } on success, or { success: false, fatalError?: string }
   * on failure. `fatalError` is set when the server returned a deterministic
   * error (e.g. chunk 0's init segment didn't parse) where retrying wouldn't
   * help — retries are skipped and the caller surfaces a distinct message.
   */
  const uploadChunkWithRetry = useCallback(async (blob, chunkIndex, sessionId) => {
    const maxRetries = 4;
    const baseDelay = 2000; // 2 seconds

    // The blob's `type` is set from the MediaRecorder's mimeType (which
    // may include codec params like ';codecs=opus'). Derive the on-disk
    // extension from the family so the backend can store + look up
    // chunks correctly. Default to webm for safety.
    const blobType = blob.type || 'audio/webm';
    const family = blobType.split(';')[0].trim().toLowerCase();
    const ext = family === 'audio/mp4' ? '.mp4' : '.webm';

    // #88: if the page is hidden (phone call / lock screen / closing),
    // a normal XHR may be killed mid-flight. sendBeacon survives page
    // death; fire it first and still run the normal upload — the server
    // dedupes by chunk_index, so a double-delivery is harmless.
    if (typeof document !== 'undefined'
        && document.visibilityState === 'hidden'
        && navigator.sendBeacon) {
      try {
        const beaconForm = new FormData();
        beaconForm.append('chunk', blob, `chunk_${chunkIndex}${ext}`);
        beaconForm.append('chunk_index', chunkIndex.toString());
        beaconForm.append('mime_type', blobType);
        const sent = navigator.sendBeacon(
          `${process.env.REACT_APP_BACKEND_URL || ''}/api/drafts/streaming/${sessionId}/audio-chunk`,
          beaconForm
        );
        console.log(`[StreamingTranscription] sendBeacon fallback for chunk ${chunkIndex}: ${sent ? 'queued' : 'rejected'}`);
      } catch (e) {
        console.warn('[StreamingTranscription] sendBeacon failed', e);
      }
    }

    for (let attempt = 0; attempt <= maxRetries; attempt++) {
      try {
        const formData = new FormData();
        formData.append('chunk', blob, `chunk_${chunkIndex}${ext}`);
        formData.append('chunk_index', chunkIndex.toString());
        formData.append('mime_type', blobType);

        await api.post(`/drafts/streaming/${sessionId}/audio-chunk`, formData, {
          headers: { 'Content-Type': 'multipart/form-data' },
          timeout: 600000,
        });

        console.log(`[StreamingTranscription] Upload complete: chunk=${chunkIndex} mime=${blobType}` + (attempt > 0 ? ` (after ${attempt} retries)` : ''));
        return { success: true };
      } catch (err) {
        // #320: the session no longer takes chunks — it was completed or
        // discarded from another tab. Retrying cannot help.
        const code = err.response?.data?.code;
        if (code === 'session_not_active' || code === 'session_not_found') {
          console.warn(`[StreamingTranscription] Session rejected chunk ${chunkIndex} (${code}); not retrying`);
          return { success: false, fatalError: SESSION_ENDED_MESSAGE, sessionEnded: true };
        }

        // Server rejected chunk 0 because it couldn't parse the format's
        // init segment. Retrying will yield the exact same bytes, so
        // short-circuit with a fatal-error message the caller can surface
        // distinctly from ordinary network flakiness. After the MP4
        // fallback fix, this path means corruption in transit (or a
        // browser producing genuinely malformed output across both
        // formats), not a browser-incompatibility issue.
        if (err.response?.data?.code === 'init_parse_failed') {
          const detail = err.response?.data?.detail || 'unknown parse error';
          console.error(`[StreamingTranscription] Fatal: server could not parse chunk ${chunkIndex}'s init segment (${detail}); not retrying`);
          return {
            success: false,
            fatalError: `Your audio recording could not be processed. Please try recording again. (${detail})`,
          };
        }

        if (attempt < maxRetries) {
          const delay = baseDelay * Math.pow(2, attempt); // 2s, 4s, 8s, 16s
          console.warn(`[StreamingTranscription] Upload failed (attempt ${attempt + 1}/${maxRetries + 1}): chunk=${chunkIndex}, retrying in ${delay}ms...`, err.message);
          await new Promise(resolve => setTimeout(resolve, delay));
        } else {
          console.error(`[StreamingTranscription] Upload FAILED after ${maxRetries + 1} attempts: chunk=${chunkIndex}`, err);
          return { success: false };
        }
      }
    }
    return { success: false };
  }, []);

  // Handle chunk upload
  const uploadChunk = useCallback(async (blob, chunkIndex) => {
    if (!sessionIdRef.current) {
      console.error('[StreamingTranscription] Cannot upload chunk: session not initialized');
      return;
    }

    const sessionId = sessionIdRef.current;
    console.log(`[StreamingTranscription] Starting upload: chunk=${chunkIndex}, blobSize=${blob.size}, session=${sessionId}`);

    const uploadPromise = (async () => {
      const result = await uploadChunkWithRetry(blob, chunkIndex, sessionId);

      if (result.success) {
        setUploadedChunks(prev => prev + 1);
        totalChunksRef.current = Math.max(totalChunksRef.current, chunkIndex + 1);
      } else if (result.fatalError && sessionIdRef.current !== sessionId) {
        // A late fatal answer for a session this tab no longer records
        // (a retry that outlived cancelStreaming) must not stop the
        // recording that replaced it.
        console.warn(`[StreamingTranscription] Ignoring fatal upload result for old session ${sessionId}`);
      } else if (result.sessionEnded) {
        stopEndedSession(sessionId);
      } else {
        // Queue for reconnect retry only if the failure wasn't fatal —
        // retrying a parse-failed chunk will produce the same bytes and
        // the same 500, so it would just loop on every online event.
        if (!result.fatalError) {
          failedChunksRef.current.push({ blob, chunkIndex, sessionId });
        } else {
          // Stop the MediaRecorder so the mic turns off and no further
          // chunks are recorded into a doomed session. The user has already
          // been shown a device-compatibility error; letting them keep
          // talking into the void would be worse than silent.
          if (resetMediaRecorderRef.current) {
            resetMediaRecorderRef.current();
          }
          setSessionState('error');
          setErrorMessage(result.fatalError);
        }
        playErrorSound();
        if (onError) {
          const uploadErr = new Error(
            result.fatalError
            || `Failed to upload chunk ${chunkIndex} after retries`
          );
          // Tag fatal errors so the parent can distinguish them from
          // recoverable transient failures and surface a toast +
          // reset phase. Non-fatal uploads (transient network) keep the
          // existing behavior of just queueing for retry without UI.
          if (result.fatalError) {
            try { uploadErr.fatal = true; } catch (_) { /* sealed */ }
          }
          onError(uploadErr);
        }
      }
    })();

    pendingUploadsRef.current.push(uploadPromise);
  }, [onError, uploadChunkWithRetry, stopEndedSession]);

  // Streaming media recorder
  const {
    status: recorderStatus,
    mediaBlob,
    mediaUrl,
    duration,
    chunkCount,
    error: recorderError,
    interrupted,
    startRecording: startMediaRecorder,
    stopRecording: stopMediaRecorder,
    pauseRecording,
    resumeRecording,
    resetRecording: resetMediaRecorder,
    getTotalChunks,
    getPartialBlob,
  } = useStreamingMediaRecorder({
    chunkIntervalMs,
    onChunkReady: uploadChunk,
    onInterruption: handleInterruption,
  });

  // Clear the interruption toast once the episode ends (resume/stop/reset).
  useEffect(() => {
    if (!interrupted && interruptionToastRef.current) {
      removeToast(interruptionToastRef.current);
      interruptionToastRef.current = null;
    }
  }, [interrupted, removeToast]);

  // #245: audio often can't reach the user while the OS holds the mic (the
  // phone is on a call, or the screen is locked) — so when the page comes
  // back to the foreground with the interruption still unresolved, re-fire
  // the audible alert. The toast is already on screen at that point.
  useEffect(() => {
    if (!interrupted) return;
    const onVisible = () => {
      if (document.visibilityState === 'visible') {
        playInterruptionAlert();
      }
    };
    document.addEventListener('visibilitychange', onVisible);
    return () => document.removeEventListener('visibilitychange', onVisible);
  }, [interrupted]);

  // Don't strand a long-lived toast if the recording UI unmounts.
  useEffect(() => () => {
    if (interruptionToastRef.current) {
      removeToast(interruptionToastRef.current);
    }
  }, [removeToast]);

  // Populate the indirection ref so uploadChunk can stop the recorder
  // on a fatal upload error (declared above, resetMediaRecorder not yet
  // in scope there).
  resetMediaRecorderRef.current = resetMediaRecorder;
  stopMediaRecorderRef.current = stopMediaRecorder;

  // SSE subscription for transcription updates (draft-based)
  const {
    isConnected: sseConnected,
    isComplete: transcriptionComplete,
    finalContent,
    draftContent,
    disconnect: disconnectSSE,
    reset: resetSSE,
  } = useDraftTranscriptionSSE(sessionId, {
    enabled: sessionState === 'recording' || sessionState === 'finalizing',
    onChunkComplete: (data) => {
      setTranscribedChunks(prev => prev + 1);
    },
    onContentUpdate: (data) => {
      // Update transcript with content from server
      setTranscript(data.content);
      if (onTranscriptUpdate) {
        onTranscriptUpdate(data.content);
      }
    },
    onAllComplete: (data) => {
      // #320: this tab never asked to finalize, so another view ended the
      // session (it recovered it as "interrupted") while this tab was still
      // recording. Not a finished recording: stop, and send nothing on.
      if (!finalizeRequestedRef.current) {
        stopEndedSession(sessionIdRef.current);
        return;
      }
      setSessionState('complete');
      setTranscript(data.content);
      if (onComplete) {
        onComplete({
          draftId: draftIdRef.current,
          sessionId: sessionIdRef.current,
          content: data.content,
          llmNodeId: data.llm_node_id || null,
          // Server-side warning (e.g. misconfigured {user_export}
          // placeholder rejected before LLM dispatch). Voice session
          // surfaces this as a toast.
          warning: data.warning || null,
        });
      }
    },
    onError: (data) => {
      console.error('Transcription error:', data);
      playErrorSound();
      if (onError) {
        onError(new Error(data.error || 'Transcription failed'));
      }
    },
  });

  resetSSERef.current = resetSSE;

  // Update internal transcript when draft content changes from SSE
  // Note: Don't call onTranscriptUpdate here - it's already called in onContentUpdate callback.
  // Calling it here too causes duplicate updates, and the effect can run AFTER onComplete
  // due to React's effect timing, overwriting the combined content with just the new transcript.
  useEffect(() => {
    if (draftContent) {
      setTranscript(draftContent);
    }
  }, [draftContent]);

  // Handle transcription completion
  useEffect(() => {
    // An all_complete this tab did not ask for was handled as "ended
    // elsewhere" in onAllComplete (#320); it must not turn into complete.
    if (!finalizeRequestedRef.current) return;
    if (transcriptionComplete && finalContent) {
      setSessionState('complete');
      setTranscript(finalContent);
    }
  }, [transcriptionComplete, finalContent]);

  // iOS suspends SSE connections when backgrounded, so the all_complete event
  // can be missed. Poll the status endpoint as a reliable fallback while in
  // 'finalizing' state. On foreground, poll immediately; otherwise every 5s.
  // This replaces the old one-shot visibility check which raced with the
  // stopStreaming promise chain resuming after iOS suspension.
  useEffect(() => {
    if (sessionState !== 'finalizing') return;
    if (!sessionIdRef.current) return;

    let pollTimer = null;
    let cancelled = false;
    // One status check at a time, and onComplete at most once per finalize.
    // One return to the page can fire two events (a back/forward-cache
    // restore fires visibilitychange and then pageshow), and the 5 s timer
    // can overlap a return. clearTimeout does not stop a request already in
    // flight, so without these flags two answers that both say 'completed'
    // would both call onComplete (in Voice mode: two POST /voice, two user
    // nodes, two billed replies). The flags belong to this effect run, so the
    // next recording's finalize starts with both cleared.
    let inFlight = false;
    let done = false;

    const checkStatus = async () => {
      if (cancelled || done || inFlight || !sessionIdRef.current) return;
      inFlight = true;
      try {
        const res = await api.get(`/drafts/streaming/${sessionIdRef.current}/status`);
        if (cancelled) return;
        if (res.data.streaming_status === 'completed' && res.data.content) {
          console.log('[StreamingTranscription] Polling recovery: transcription already complete');
          done = true;
          disconnectSSE();
          setSessionState('complete');
          setTranscript(res.data.content);
          if (onCompleteRef.current) {
            onCompleteRef.current({
              draftId: draftIdRef.current,
              sessionId: sessionIdRef.current,
              content: res.data.content,
              llmNodeId: res.data.llm_node_id || null,
              warning: res.data.warning || null,
            });
          }
          return; // Done — don't schedule another poll
        }
      } catch (err) {
        console.error('[StreamingTranscription] Polling status check failed:', err);
      } finally {
        inFlight = false;
      }
      // Not completed yet — schedule another check
      if (!cancelled) {
        pollTimer = setTimeout(checkStatus, 5000);
      }
    };

    // On a return to the page (shown again, restored from the
    // back/forward cache, back online), poll immediately (iOS resumes JS
    // but SSE is dead)
    const stopListening = onPageReturn(() => {
      // Clear any pending delayed poll and check immediately
      if (pollTimer) clearTimeout(pollTimer);
      checkStatus();
    });

    // Start first poll after a short delay (give SSE a chance to deliver first)
    pollTimer = setTimeout(checkStatus, 5000);

    return () => {
      cancelled = true;
      if (pollTimer) clearTimeout(pollTimer);
      stopListening();
    };
  }, [sessionState, disconnectSSE]);

  // Online/offline detection and retry failed chunks
  useEffect(() => {
    const handleOnline = async () => {
      setIsOffline(false);
      console.log('[StreamingTranscription] Network back online');

      // Retry failed chunks
      if (failedChunksRef.current.length > 0) {
        console.log(`[StreamingTranscription] Retrying ${failedChunksRef.current.length} failed chunks...`);
        const chunksToRetry = [...failedChunksRef.current];
        failedChunksRef.current = [];

        for (const { blob, chunkIndex, sessionId } of chunksToRetry) {
          const result = await uploadChunkWithRetry(blob, chunkIndex, sessionId);
          if (result.success) {
            setUploadedChunks(prev => prev + 1);
            totalChunksRef.current = Math.max(totalChunksRef.current, chunkIndex + 1);
          } else if (!result.fatalError) {
            // Still failing for a retryable reason — put it back for the
            // next online event. Fatal errors (e.g. webm_header_parse_failed)
            // are deterministic, so we drop them rather than loop.
            failedChunksRef.current.push({ blob, chunkIndex, sessionId });
          }
        }

        if (failedChunksRef.current.length > 0) {
          console.warn(`[StreamingTranscription] ${failedChunksRef.current.length} chunks still failing after online retry`);
        }
      }
    };

    const handleOffline = () => {
      setIsOffline(true);
      console.log('[StreamingTranscription] Network went offline');
    };

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);
    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, [uploadChunkWithRetry]);

  // #320: a tab that goes away mid-recording (reload, close, navigating
  // off the site) releases its session, so the reloaded page offers
  // recovery at once. Without this the server keeps treating the session
  // as live — and hides it from recovery — for its liveness window.
  // The same when the component recording goes away in-app (text mode's
  // StreamingMicButton on SPA navigation has no cleanup of its own).
  useEffect(() => {
    window.addEventListener('pagehide', releaseIfAbandoned);
    return () => {
      window.removeEventListener('pagehide', releaseIfAbandoned);
      releaseIfAbandoned();
    };
  }, [releaseIfAbandoned]);

  // NOTE: Emergency chunk upload on page unload was attempted using both
  // sendBeacon and fetch+keepalive, but Chrome fires ondataavailable from
  // requestData() asynchronously, so there's no synchronous way to extract
  // buffered audio from MediaRecorder during page teardown. The beforeunload
  // dialog gives users a chance to stay, and the recovery banner on Reflect/
  // Orient pages lets them continue the session. At most 15s of audio
  // (one chunk interval) is lost on unexpected page unload.

  // Initialize streaming session (creates draft, NOT node)
  // overrideParentId allows callers to pass the current parent at call time
  // (useful when parentId changes between recording turns)
  const initSession = useCallback(async (overrideParentId) => {
    setSessionState('initializing');
    setErrorMessage(null);

    try {
      const response = await api.post('/drafts/streaming/init', {
        parent_id: overrideParentId !== undefined ? overrideParentId : parentId,
        privacy_level: privacyLevel,
        ai_usage: aiUsage,
        label: label || undefined,
      });

      const { draft_id, session_id } = response.data;

      setDraftId(draft_id);
      setSessionId(session_id);
      draftIdRef.current = draft_id;
      sessionIdRef.current = session_id;

      return { draftId: draft_id, sessionId: session_id };

    } catch (err) {
      console.error('Failed to initialize streaming session:', err);
      if (isSpendCapError(err)) {
        // Monthly spend cap (#341): the server refused before any draft
        // existed and before the mic opened. Nothing failed, so go back
        // to idle and say why (the api.js interceptor already raised the
        // banner). `spendCapped` + `startup` let the parent reset its UI
        // without treating this as an error.
        setSessionState('idle');
        addToast(spendCapToastMessage('record'), 8000);
        try {
          err.spendCapped = true;
          err.startup = true;
        } catch (_) { /* sealed error object */ }
        if (onError) onError(err);
        throw err;
      }
      if (isAiUsageRefusedError(err)) {
        // A Voice recording where AI may not read (AI usage 'none' on the
        // account or the thread): the server refused before any draft
        // existed and before the mic opened. Nothing failed; the parent
        // shows why instead of the record button. No toast here.
        setSessionState('idle');
        try {
          err.aiUsageRefused = true;
          err.aiUsageScope = aiUsageRefusalScope(err);
          err.startup = true;
        } catch (_) { /* sealed error object */ }
        if (onError) onError(err);
        throw err;
      }
      setSessionState('error');
      setErrorMessage(err.message);
      if (onError) {
        onError(err);
      }
      throw err;
    }
  }, [parentId, privacyLevel, aiUsage, label, onError, addToast]);

  // Start streaming transcription
  // overrideParentId: optional parent ID to use instead of the hook's parentId
  const startStreaming = useCallback(async (overrideParentId) => {
    let initSucceeded = false;
    finalizeRequestedRef.current = false;
    releasedSessionRef.current = null;
    // A new session starts from fresh SSE state even when the previous
    // one ended without cancelStreaming (e.g. voice mode back to ready).
    resetSSE();
    try {
      await initSession(overrideParentId);
      initSucceeded = true;
      await startMediaRecorder();
      setSessionState('recording');

    } catch (err) {
      console.error('Failed to start streaming:', err);
      // Refused by the spend cap or by AI usage: initSession already
      // returned to idle and told the parent. The mic was never requested.
      if (err?.spendCapped || err?.aiUsageRefused) return;
      // Always land in a terminal state. The error path here means recording
      // never started, so the session must not be left in 'recording' or
      // 'initializing' (which would strand the parent UI). 'error' is set
      // below; permission denials with an orphaned draft are cleaned up first.
      const name = err?.name || err?.error?.name;
      const isPermissionDenial =
        name === 'NotAllowedError' || name === 'PermissionDeniedError';

      // If initSession created a draft but recording could not start because
      // the mic permission was denied, the draft is a dead streaming session
      // (streaming_status='recording', zero chunks). Best-effort discard it so
      // the "resume interrupted session" recovery banner — which lists drafts
      // with streaming_status='recording' — doesn't later offer this empty,
      // doomed session. The discard endpoint is keyed by session_id (not
      // draft_id) and removes the draft, its chunk records, and audio dir.
      // Failures here are non-fatal (network/permissions).
      if (isPermissionDenial && initSucceeded && sessionIdRef.current) {
        try {
          await api.delete(`/drafts/streaming/${sessionIdRef.current}/discard`);
          console.log('[StreamingTranscription] Discarded orphaned draft after permission denial (session):', sessionIdRef.current);
        } catch (delErr) {
          console.warn('[StreamingTranscription] Failed to discard orphaned draft after permission denial (non-fatal):', delErr?.message);
        }
      }

      setSessionState('error');
      setErrorMessage(err.message);
      // initSession's catch already invokes onError before re-throwing, so
      // only fire it here for post-init failures (e.g. getUserMedia rejected
      // because mic permission was denied or the mic is held by another app).
      // Without this, the parent never learns recording failed and leaves
      // the UI stuck in the "recording" phase. Tag with `startup: true` so
      // the parent can distinguish from runtime chunk-upload errors and
      // safely reset its UI without disrupting an in-progress recording.
      if (initSucceeded && onError) {
        try { err.startup = true; } catch (_) { /* DOMException may be sealed */ }
        onError(err);
      }
    }
  }, [initSession, startMediaRecorder, onError, resetSSE]);

  // Resume an existing interrupted session (continue recording).
  // New chunks start after existingChunkCount, duration continues from estimated offset.
  // existingMimeType: family-only mime ('audio/webm' or 'audio/mp4') from the
  //   /drafts/interrupted payload. The recorder must record in the same family
  //   chunk 0 was uploaded with, otherwise the server rejects subsequent chunks
  //   with mime_mismatch. If this browser can't record that family, the
  //   recorder hook throws NotSupportedError and we surface that via onError.
  const resumeStreaming = useCallback(async (existingSessionId, existingDraftId, existingChunkCount, existingMimeType) => {
    let initSucceeded = false;
    finalizeRequestedRef.current = false;
    releasedSessionRef.current = null;
    try {
      setSessionState('initializing');
      setErrorMessage(null);

      setDraftId(existingDraftId);
      setSessionId(existingSessionId);
      draftIdRef.current = existingDraftId;
      sessionIdRef.current = existingSessionId;
      totalChunksRef.current = existingChunkCount || 0;
      initSucceeded = true;

      const durationOffset = (existingChunkCount || 0) * (chunkIntervalMs / 1000);
      await startMediaRecorder({
        startingChunkIndex: existingChunkCount || 0,
        durationOffset,
        forceMimeFamily: existingMimeType || null,
      });
      setSessionState('recording');
    } catch (err) {
      console.error('Failed to resume streaming:', err);
      setSessionState('error');
      setErrorMessage(err.message);
      // Mirror startStreaming's onError plumbing so the parent sees the
      // resume-incompatible-browser case and can reset the UI.
      if (initSucceeded && onError) {
        try { err.startup = true; } catch (_) { /* DOMException may be sealed */ }
        onError(err);
      }
    }
  }, [startMediaRecorder, chunkIntervalMs, onError]);

  // Stop streaming and finalize
  // extraParams: optional { parent_id, model } for server-side LLM chain
  const stopStreaming = useCallback(async (extraParams) => {
    console.log(`[StreamingTranscription] stopStreaming called: session=${sessionIdRef.current}, pendingUploads=${pendingUploadsRef.current.length}`);

    finalizeRequestedRef.current = true;
    setSessionState('finalizing');

    // Stop recording and wait for the final ondataavailable handler to complete.
    // This guarantees the final chunk has been emitted and its upload enqueued
    // in pendingUploadsRef before we proceed.
    await stopMediaRecorder();

    // Now wait for ALL pending uploads to complete (including the final chunk's upload)
    const uploadsToWait = [...pendingUploadsRef.current];
    console.log(`[StreamingTranscription] Waiting for ${uploadsToWait.length} pending uploads to complete...`);
    await Promise.allSettled(uploadsToWait);

    // Get total chunks — should now include the final chunk
    const totalChunks = getTotalChunks();
    console.log(`[StreamingTranscription] All uploads settled. totalChunks=${totalChunks}, uploadedChunks=${totalChunksRef.current}`);

    try {
      // Call finalize endpoint
      const finalizePayload = { total_chunks: totalChunks };
      if (label) finalizePayload.label = label;
      // Include workflow params for server-side LLM + TTS chain
      if (extraParams?.parent_id) finalizePayload.parent_id = extraParams.parent_id;
      if (extraParams?.model) finalizePayload.model = extraParams.model;
      // A fresh Voice thread started from the Glean card (#435).
      if (extraParams?.entry) finalizePayload.entry = extraParams.entry;
      await api.post(`/drafts/streaming/${sessionIdRef.current}/finalize`,
        finalizePayload, {
        timeout: 120000, // 2 minutes for finalize (just queues the task)
      });

      console.log(`[StreamingTranscription] Finalize request sent: total_chunks=${totalChunks}`);
      // SSE will notify when complete
      // Keep finalizing state until SSE signals completion

    } catch (err) {
      console.error(`[StreamingTranscription] Finalize FAILED:`, err);
      playErrorSound();
      setErrorMessage(err.message);
      if (onError) {
        onError(err);
      }
    }
  }, [stopMediaRecorder, getTotalChunks, onError, label]);

  // Save the streaming draft as a node
  const saveAsNode = useCallback(async (editedContent = null) => {
    if (!sessionIdRef.current) {
      throw new Error('No streaming session to save');
    }

    try {
      const response = await api.post(`/drafts/streaming/${sessionIdRef.current}/save-as-node`, {
        content: editedContent || transcript,
      });

      return response.data;
    } catch (err) {
      console.error('Failed to save streaming draft as node:', err);
      if (onError) {
        onError(err);
      }
      throw err;
    }
  }, [transcript, onError]);

  // Cancel/reset everything
  const cancelStreaming = useCallback(() => {
    // Abandoning a recording this tab never finalized (e.g. the voice page
    // unmounts mid-recording): release it so it is recoverable right away
    // (#320). The server ignores this for a session no longer recording.
    releaseIfAbandoned();
    finalizeRequestedRef.current = false;
    disconnectSSE();
    resetMediaRecorder();
    resetSSE();

    setSessionState('idle');
    setDraftId(null);
    setSessionId(null);
    setTranscript('');
    setUploadedChunks(0);
    setTranscribedChunks(0);
    setErrorMessage(null);

    draftIdRef.current = null;
    sessionIdRef.current = null;
    totalChunksRef.current = 0;
    pendingUploadsRef.current = [];
    failedChunksRef.current = [];
  }, [disconnectSSE, resetMediaRecorder, resetSSE, releaseIfAbandoned]);

  return {
    // State
    sessionState,
    draftId,
    sessionId,
    transcript,
    duration,
    chunkCount,
    uploadedChunks,
    transcribedChunks,
    errorMessage,
    recorderError,

    // Connection status
    isRecording: recorderStatus === 'recording',
    isPaused: recorderStatus === 'paused',
    isInterrupted: interrupted,
    isSSEConnected: sseConnected,
    isOffline,

    // Media
    mediaBlob,
    mediaUrl,

    // Actions
    startStreaming,
    resumeStreaming,
    stopStreaming,
    pauseRecording,
    resumeRecording,
    saveAsNode,
    cancelStreaming,
    getPartialBlob,
  };
}
